import { useState, useEffect, useContext, forwardRef } from "react";
import { useTranslation } from "react-i18next";

import "../InvoicesManagement.scss";
import useLanguage from "../../utils/useLanguage";

// Import education level service
import { getAllEducationLevels } from "../../services/educationLevelServices";

import CustomInput from "../../components/CustomInput";
import CustomButton from "../../components/CustomButton";
import InformationBox from "../../components/InformationBox";
import BackToDashboard from "../../components/BackToDashboard";
import Loader from "../../components/Loader";
import RoundedTable from "../../components/table-component/RoundedTable";
import { TABLE_CELL_TYPES } from "../../components/table-component/RoundedTable";

import previousIcon from "../../assets/previous_icon.png";
import nextIcon from "../../assets/next_icon.png";
import addIconLight from "../../assets/add_icon_light.png";
import searchIcon from "../../assets/search_icon.png";
import infoIcon from "../../assets/information.png";
import AddEduStageModal from "./AddEduStageModal";
import EducationLevelInfoModal from "./EducationLevelInfoModal";

const EducationStagesPage = forwardRef((props, ref) => {
  // Context and hooks
  const { t } = useTranslation("general");
  const { currentDirection } = useLanguage();

  const [loading, setLoading] = useState(false);

  // State variables
  const [searchQuery, setSearchQuery] = useState("");
  const [educationLevels, setEducationLevels] = useState([]);
  const [showAddStageModal, setShowAddStageModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [selectedEducationLevel, setSelectedEducationLevel] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalRecords: 0,
    totalPages: 0,
  });

  // Constants
  const tableTitles = [
    { value: "educationStageName", keepOnMobile: true },
    { value: "price" },
    { value: "theOptions", keepOnMobile: true },
  ];

  // Handlers
  const handleViewInfoClick = (educationLevel) => {
    setSelectedEducationLevel(educationLevel);
    setShowInfoModal(true);
  };

  // Data fetching
  const getAllEducationLevelsData = async () => {
    try {
      const params = {
        page: pagination.currentPage,
        limit: 10,
        search: searchQuery,
      };
      setLoading(true);
      const response = await getAllEducationLevels(params);

      if (response?.status === 200) {
        const levels = response.data.classes;
        setPagination({
          currentPage: pagination.currentPage,
          totalPages: response?.data?.totalPages,
          totalRecords: response?.data?.totalRecords,
        });

        const formattedEducationLevels = levels.map((level) => [
          { value: level.name, type: TABLE_CELL_TYPES.TEXT,
            keepOnMobile: true
           },
          {
            value: level.price,
            type: TABLE_CELL_TYPES.MONEY,
            currency: `${t("IQD")}`,
          },
          {
            value: (
              <CustomButton
                variant="blank"
                title={t("ViewInfo")}
                onClick={() => handleViewInfoClick(level)}
                icon={infoIcon}
                iconSize={15}
              />
            ),
            type: TABLE_CELL_TYPES.ELEMENT,
            keepOnMobile: true
          },
        ]);

        setEducationLevels(formattedEducationLevels);
      }
    } catch (error) {
      console.error(
        "Error fetching education levels:",
        error.response?.data || error
      );
    } finally {
      setLoading(false);
    }
  };

  // Handle search
  const handleSearch = () => {
    getAllEducationLevelsData();
  };

  // Handle successful creation
  const handleEducationLevelCreated = () => {
    getAllEducationLevelsData();
  };

  // Effects
  useEffect(() => {
    getAllEducationLevelsData();
  }, [pagination.currentPage]);

  return (
    <>
      <main className="e-container">
        <BackToDashboard
          title={t("back")}
          variant="blank"
          icon={currentDirection === "ltr" ? previousIcon : nextIcon}
          iconSize={14}
          to="/students"
        />
        <div className="invoice-table-container">
          <div className="under-user-section">
            <div className="page-title">{t("stages")}</div>
          </div>

          {loading ? (
            <Loader />
          ) : (
            <>
              <div className="u-margin--bottom_300">
                <div className=" o-row o-row--horizontal-align_end">
                  <CustomButton
                    title={t("addAnEduStage")}
                    onClick={() => setShowAddStageModal(true)}
                    icon={addIconLight}
                    iconSize={20}
                  />
                </div>
              </div>

              <div className="c-search-bar u-margin--bottom_500">
                <section className="c-search-bar-label-input">
                  <label>{t("search")}:</label>
                  <CustomInput
                    placeholder={t("search")}
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                    }}
                    resetSearch={() => {
                      setSearchQuery("");
                    }}
                    thick
                  />
                </section>

                {/* <section className="col button-col"> */}
                  <CustomButton
                    title={t("search")}
                    icon={searchIcon}
                    iconSize={15}
                    onClick={handleSearch}
                  />
                {/* </section> */}
              </div>

              {educationLevels.length > 0 ? (
                <RoundedTable
                  titles={tableTitles}
                  rows={educationLevels}
                  pagination={pagination}
                  setPagination={setPagination}
                />
              ) : (
                <InformationBox>{t("noEducationLevelsFound")}</InformationBox>
              )}
            </>
          )}
        </div>
      </main>

      {showAddStageModal && (
        <AddEduStageModal
          setShowAddStageModal={setShowAddStageModal}
          onSuccess={handleEducationLevelCreated}
        />
      )}

      {showInfoModal && selectedEducationLevel && (
        <EducationLevelInfoModal
          educationLevel={selectedEducationLevel}
          onClose={() => setShowInfoModal(false)}
          onUpdate={handleEducationLevelCreated}
        />
      )}
    </>
  );
});

export default EducationStagesPage;
